import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useConfirm } from "../contexts/ConfirmContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const About = () => {
  const { confirm } = useConfirm();
  const [about, setAbout] = useState({
    mission: "",
    vision: "",
    features: [],
    team: [],
    partners: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [savedItems, setSavedItems] = useState({
    features: {},
    team: {},
    partners: {},
  });
  const [editingMember, setEditingMember] = useState(null);
  const [showMemberForm, setShowMemberForm] = useState(false);

  useEffect(() => {
    fetchAbout();
  }, []);

  // Global test function for debugging (can be called from browser console)
  window.testImageUpload = async () => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      // Create a test image (1x1 pixel PNG)
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "red";
      ctx.fillRect(0, 0, 1, 1);

      canvas.toBlob((blob) => {
        formData.append("image", blob, "test.png");

        axios
          .post(`${API_URL}/upload/profile-image`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            console.log("Test upload successful:", response.data);
            toast.success("Test upload successful! Check console for details.");
          })
          .catch((error) => {
            console.error("Test upload failed:", error);
            toast.error("Test upload failed! Check console for details.");
          });
      }, "image/png");
    } catch (error) {
      console.error("Test upload error:", error);
      toast.error("Test upload error! Check console for details.");
    }
  };

  const fetchAbout = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/about`);
      console.log("Fetched about data:", response.data);

      if (response.data) {
        // Ensure features, team, and partners are arrays
        const aboutData = {
          ...response.data,
          features: Array.isArray(response.data.features)
            ? response.data.features
            : [],
          team: Array.isArray(response.data.team) ? response.data.team : [],
          partners: Array.isArray(response.data.partners)
            ? response.data.partners
            : [],
        };

        console.log("Processed about data:", aboutData);
        console.log("Team members:", aboutData.team);
        setAbout(aboutData);
      }
    } catch (error) {
      console.error("Error fetching about content:", error);
      // Set default data if API fails
      setAbout({
        mission:
          "Our mission is to provide high-quality education and training to empower individuals with the skills they need to succeed in their careers.",
        vision:
          "To be the leading platform for professional development and lifelong learning.",
        features: [
          {
            title: "Expert Instructors",
            description:
              "Learn from industry professionals with years of experience",
          },
          {
            title: "Flexible Learning",
            description: "Study at your own pace, anytime and anywhere",
          },
          {
            title: "Practical Skills",
            description: "Gain hands-on experience with real-world projects",
          },
        ],
        team: [],
        partners: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      if (about.id) {
        await axios.put(`${API_URL}/about/${about.id}`, about, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("About content updated successfully");
      } else {
        await axios.post(`${API_URL}/about`, about, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("About content created successfully");
      }
      fetchAbout();
    } catch (error) {
      console.error("Error saving about content:", error);
      toast.error("Failed to save about content");
    } finally {
      setSaving(false);   }
  };

  const handleSaveMember = async (memberIndex) => {
    try {
      const token = localStorage.getItem("token");
      const member = about.team[memberIndex];

      // Validate required fields
      if (!member.name || !member.position) {
        toast.error("Name and Position are required for team members");
        return;
      }

      // Ensure all team member fields are properly structured
      const updatedTeam = [...about.team];
      updatedTeam[memberIndex] = {
        name: member.name || "",
        position: member.position || "",
        bio: member.bio || "",
        email: member.email || "",
        linkedin: member.linkedin || "",
        twitter: member.twitter || "",
        image: member.image || "",
      };

      const updatedAbout = {
        ...about,
        team: updatedTeam,
      };

      console.log("Saving team member:", updatedTeam[memberIndex]);
      console.log("Updated about data:", updatedAbout);

      if (about.id) {
        const response = await axios.put(
          `${API_URL}/about/${about.id}`,
          updatedAbout,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Update response:", response.data);
        toast.success(`Team member "${member.name}" saved successfully`);
      } else {
        const response = await axios.post(`${API_URL}/about`, updatedAbout, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log("Create response:", response.data);
        toast.success(`Team member "${member.name}" saved successfully`);
      }

      // Mark as saved
      setSavedItems((prev) => ({
        ...prev,
        team: { ...prev.team, [memberIndex]: true },
      }));

      // Close the form if it's open
      setShowMemberForm(false);
      setEditingMember(null);

      // Refresh the data
      await fetchAbout();
    } catch (error) {
      console.error("Error saving team member:", error);
      console.error("Error details:", error.response?.data);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save team member";
      toast.error(errorMessage);
    }
  };

  const handleSaveFeature = async (featureIndex) => {
    try {
      const token = localStorage.getItem("token");
      const feature = about.features[featureIndex];

      // Validate required fields
      if (!feature.title || !feature.description) {
        toast.error("Title and Description are required for features");
        return;
      }

      // Update the about content with the current feature
      const updatedAbout = {
        ...about,
        features: [...about.features], // Create a copy to ensure state update
      };

      if (about.id) {
        await axios.put(`${API_URL}/about/${about.id}`, updatedAbout, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success(`Feature "${feature.title}" saved successfully`);
      } else {
        await axios.post(`${API_URL}/about`, updatedAbout, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success(`Feature "${feature.title}" saved successfully`);
      }

      // Mark as saved
      setSavedItems((prev) => ({
        ...prev,
        features: { ...prev.features, [featureIndex]: true },
      }));

      // Refresh the data
      fetchAbout();
    } catch (error) {
      console.error("Error saving feature:", error);
      toast.error("Failed to save feature");
    }
  };

  const handleSavePartner = async (partnerIndex) => {
    try {
      const token = localStorage.getItem("token");
      const partner = about.partners[partnerIndex];

      // Validate required fields
      if (!partner.name) {
        toast.error("Partner name is required");
        return;
      }

      // Update the about content with the current partner
      const updatedAbout = {
        ...about,
        partners: [...about.partners], // Create a copy to ensure state update
      };

      if (about.id) {
        await axios.put(`${API_URL}/about/${about.id}`, updatedAbout, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success(`Partner "${partner.name}" saved successfully`);
      } else {
        await axios.post(`${API_URL}/about`, updatedAbout, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success(`Partner "${partner.name}" saved successfully`);
      }

      // Mark as saved
      setSavedItems((prev) => ({
        ...prev,
        partners: { ...prev.partners, [partnerIndex]: true },
      }));

      // Refresh the data
      fetchAbout();
    } catch (error) {
      console.error("Error saving partner:", error);
      toast.error("Failed to save partner");
    }
  };

  const handleEditMember = (memberIndex) => {
    const member = about.team[memberIndex];
    setEditingMember({ ...member, index: memberIndex });
    setShowMemberForm(true);
  };

  const handleDeleteMember = async (memberIndex) => {
    const ok = await confirm({
      title: "Delete team member",
      message: "Are you sure you want to delete this team member?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const updatedTeam = about.team.filter(
        (_, index) => index !== memberIndex
      );
      const updatedAbout = { ...about, team: updatedTeam };
      const token = localStorage.getItem("token");
      if (about.id) {
        await axios.put(`${API_URL}/about/${about.id}`, updatedAbout, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/about`, updatedAbout, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      toast.success("Team member deleted successfully");
      fetchAbout();
    } catch (error) {
      console.error("Error deleting team member:", error);
      toast.error("Failed to delete team member");
    }
  };

  const handlePreviewMember = (member) => {
    // Open preview in a new window or modal
    const previewWindow = window.open("", "_blank", "width=600,height=400");
    previewWindow.document.write(`
      <html>
        <head>
          <title>Team Member Preview</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; background: #f5f5f5; }
            .preview-card { background: white; border-radius: 12px; padding: 24px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 400px; margin: 0 auto; }
            .profile-image { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; margin: 0 auto 16px; display: block; }
            .name { font-size: 24px; font-weight: bold; color: #1f2937; margin-bottom: 8px; text-align: center; }
            .position { color: #059669; font-weight: 600; margin-bottom: 12px; text-align: center; }
            .email { color: #6b7280; margin-bottom: 8px; text-align: center; }
            .bio { color: #374151; line-height: 1.6; margin-bottom: 16px; }
            .social-links { display: flex; justify-content: center; gap: 12px; }
            .social-link { color: #6b7280; text-decoration: none; }
            .social-link:hover { color: #059669; }
          </style>
        </head>
        <body>
          <div class="preview-card">
            ${
              member.image
                ? `<img src="${member.image}" alt="${member.name}" class="profile-image">`
                : `<div class="profile-image" style="background: linear-gradient(135deg, #059669, #10b981); display: flex; align-items: center; justify-content: center; color: white; font-size: 32px; font-weight: bold;">${
                    member.name ? member.name.charAt(0).toUpperCase() : "T"
                  }</div>`
            }
            <div class="name">${member.name || "Team Member"}</div>
            <div class="position">${member.position || "Position"}</div>
            ${member.email ? `<div class="email">${member.email}</div>` : ""}
            ${member.bio ? `<div class="bio">${member.bio}</div>` : ""}
            ${
              member.linkedin || member.twitter
                ? `
              <div class="social-links">
                ${
                  member.linkedin
                    ? `<a href="${member.linkedin}" target="_blank" class="social-link">LinkedIn</a>`
                    : ""
                }
                ${
                  member.twitter
                    ? `<a href="${member.twitter}" target="_blank" class="social-link">Twitter</a>`
                    : ""
                }
              </div>
            `
                : ""
            }
          </div>
        </body>
      </html>
    `);
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setShowMemberForm(false);
  };

  const handleUpdateMember = async () => {
    if (!editingMember.name || !editingMember.position) {
      toast.error("Name and Position are required");
      return;
    }

    try {
      const updatedTeam = [...about.team];
      updatedTeam[editingMember.index] = {
        name: editingMember.name,
        position: editingMember.position,
        bio: editingMember.bio || "",
        email: editingMember.email || "",
        linkedin: editingMember.linkedin || "",
        twitter: editingMember.twitter || "",
        image: editingMember.image || "",
      };

      const updatedAbout = {
        ...about,
        team: updatedTeam,
      };

      console.log("Updating team member:", updatedTeam[editingMember.index]);
      console.log("Updated about data:", updatedAbout);
      console.log("About ID:", about.id);
      console.log("API URL:", API_URL);

      const token = localStorage.getItem("token");
      console.log("Token present:", !!token);

      if (about.id) {
        console.log("Making PUT request to:", `${API_URL}/about/${about.id}`);
        const response = await axios.put(
          `${API_URL}/about/${about.id}`,
          updatedAbout,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Update response:", response.data);
      } else {
        console.log("Making POST request to:", `${API_URL}/about`);
        const response = await axios.post(`${API_URL}/about`, updatedAbout, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log("Create response:", response.data);
      }

      toast.success(`Team member "${editingMember.name}" updated successfully`);
      setEditingMember(null);
      setShowMemberForm(false);
      await fetchAbout();
    } catch (error) {
      console.error("Error updating team member:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error details:", error.response?.data);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update team member";
      toast.error(errorMessage);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAbout((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayChange = (name, index, field, value) => {
    setAbout((prev) => ({
      ...prev,
      [name]: prev[name].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));

    // Reset saved status when item is modified
    setSavedItems((prev) => ({
      ...prev,
      [name]: { ...prev[name], [index]: false },
    }));
  };

  const addArrayItem = (name, defaultItem = {}) => {
    setAbout((prev) => ({
      ...prev,
      [name]: [...prev[name], defaultItem],
    }));
  };

  const removeArrayItem = (name, index) => {
    setAbout((prev) => ({
      ...prev,
      [name]: prev[name].filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (file, index) => {
    try {
      setUploadingImages((prev) => ({ ...prev, [index]: true }));

      const formData = new FormData();
      formData.append("image", file);

      console.log("Uploading image for team member at index:", index);
      console.log("File details:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/upload/profile-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Upload response:", response.data);

      if (response.data.url) {
        handleArrayChange("team", index, "image", response.data.url);
        toast.success("Profile image uploaded and optimized successfully");
        console.log("Image URL saved:", response.data.url);
      } else {
        toast.error("Upload failed: No URL returned");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to upload image: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setUploadingImages((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleImageUploadForEdit = async (file, memberIndex) => {
    try {
      setUploadingImages((prev) => ({ ...prev, [memberIndex]: true }));

      const formData = new FormData();
      formData.append("image", file);

      console.log("Uploading image for editing member at index:", memberIndex);
      console.log("File details:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/upload/profile-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Upload response for edit:", response.data);

      if (response.data.url) {
        setEditingMember((prev) => ({ ...prev, image: response.data.url }));
        toast.success("Profile image uploaded and optimized successfully");
        console.log("Image URL saved for edit:", response.data.url);
      } else {
        toast.error("Upload failed: No URL returned");
      }
    } catch (error) {
      console.error("Error uploading image for edit:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to upload image: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setUploadingImages((prev) => ({ ...prev, [memberIndex]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <h1 className="text-3xl font-bold mb-8 text-gray-800 border-b pb-4">
          About Section Management
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Mission */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mission Statement
            </label>
            <textarea
              name="mission"
              value={about.mission}
              onChange={handleChange}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
              placeholder="Enter your organization's mission statement..."
              required
            />
          </div>

          {/* Vision */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vision Statement
            </label>
            <textarea
              name="vision"
              value={about.vision}
              onChange={handleChange}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
              placeholder="Enter your organization's vision statement..."
              required
            />
          </div>

          {/* Features */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Key Features
              </label>
              <button
                type="button"
                onClick={() =>
                  addArrayItem("features", { title: "", description: "" })
                }
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                Add Feature
              </button>
            </div>
            {about.features.map((feature, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border mb-4">
                <div className="grid grid-cols-1 gap-4">
                  <input
                    type="text"
                    placeholder="Feature Title"
                    value={feature.title || ""}
                    onChange={(e) =>
                      handleArrayChange(
                        "features",
                        index,
                        "title",
                        e.target.value
                      )
                    }
                    className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                  <textarea
                    placeholder="Feature Description"
                    value={feature.description || ""}
                    onChange={(e) =>
                      handleArrayChange(
                        "features",
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    rows={3}
                    className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => removeArrayItem("features", index)}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-800 bg-red-50 rounded"
                  >
                    Remove Feature
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveFeature(index)}
                    className="px-3 py-1 text-sm text-teal-600 hover:text-teal-800 bg-teal-50 rounded"
                  >
                    Save Feature
                  </button>
                  {savedItems.features[index] && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      ✓ Saved
                    </span>
                  )}
                </div>
              </div>
            ))}
            {about.features.length === 0 && (
              <p className="text-gray-500 text-center py-4">
                No features added yet. Click "Add Feature" to get started.
              </p>
            )}
          </div>

          {/* Team */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-6">
              <label className="block text-sm font-medium text-gray-700 text-lg">
                Team Members
              </label>
              <button
                type="button"
                onClick={() => {
                  setEditingMember(null);
                  setShowMemberForm(true);
                  addArrayItem("team", {
                    name: "",
                    position: "",
                    bio: "",
                    email: "",
                    linkedin: "",
                    twitter: "",
                    image: "",
                  });
                }}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                Add New Team Member
              </button>
            </div>
            {/* Saved Team Members List */}
            {about.team.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-700 mb-4">
                  Saved Team Members
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {about.team.map((member, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          {member.image ? (
                            <img
                              src={member.image}
                              alt={member.name || "Team member"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-gray-400 text-lg font-bold">
                              {member.name
                                ? member.name.charAt(0).toUpperCase()
                                : "T"}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {member.name || "Team Member"}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">
                            {member.position || "Position"}
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handlePreviewMember(member)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Preview"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditMember(index)}
                            className="p-1 text-green-600 hover:text-green-800"
                            title="Edit"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMember(index)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                        {savedItems.team[index] && (
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                            ✓ Saved
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Team Member Form */}
            {showMemberForm && (
              <div className="bg-white p-6 rounded-lg border mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingMember
                      ? `Edit Team Member: ${editingMember.name}`
                      : "Add New Team Member"}
                  </h3>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Full Name"
                        value={
                          editingMember
                            ? editingMember.name
                            : about.team[about.team.length - 1]?.name || ""
                        }
                        onChange={(e) => {
                          if (editingMember) {
                            setEditingMember({
                              ...editingMember,
                              name: e.target.value,
                            });
                          } else {
                            handleArrayChange(
                              "team",
                              about.team.length - 1,
                              "name",
                              e.target.value
                            );
                          }
                        }}
                        className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                      <input
                        type="text"
                        placeholder="Position/Role"
                        value={
                          editingMember
                            ? editingMember.position
                            : about.team[about.team.length - 1]?.position || ""
                        }
                        onChange={(e) => {
                          if (editingMember) {
                            setEditingMember({
                              ...editingMember,
                              position: e.target.value,
                            });
                          } else {
                            handleArrayChange(
                              "team",
                              about.team.length - 1,
                              "position",
                              e.target.value
                            );
                          }
                        }}
                        className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={
                        editingMember
                          ? editingMember.email
                          : about.team[about.team.length - 1]?.email || ""
                      }
                      onChange={(e) => {
                        if (editingMember) {
                          setEditingMember({
                            ...editingMember,
                            email: e.target.value,
                          });
                        } else {
                          handleArrayChange(
                            "team",
                            about.team.length - 1,
                            "email",
                            e.target.value
                          );
                        }
                      }}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                    <textarea
                      placeholder="Bio/Description"
                      value={
                        editingMember
                          ? editingMember.bio
                          : about.team[about.team.length - 1]?.bio || ""
                      }
                      onChange={(e) => {
                        if (editingMember) {
                          setEditingMember({
                            ...editingMember,
                            bio: e.target.value,
                          });
                        } else {
                          handleArrayChange(
                            "team",
                            about.team.length - 1,
                            "bio",
                            e.target.value
                          );
                        }
                      }}
                      rows={4}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>

                  {/* Right Column - Image and Social Links */}
                  <div className="space-y-4">
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Image
                      </label>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden relative">
                          {uploadingImages[
                            editingMember
                              ? editingMember.index
                              : about.team.length - 1
                          ] ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-full">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                            </div>
                          ) : (
                              editingMember
                                ? editingMember.image
                                : about.team[about.team.length - 1]?.image
                            ) ? (
                            <img
                              src={
                                editingMember
                                  ? editingMember.image
                                  : about.team[about.team.length - 1]?.image
                              }
                              alt={
                                editingMember
                                  ? editingMember.name
                                  : about.team[about.team.length - 1]?.name ||
                                    "Team member"
                              }
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-gray-400 text-2xl">
                              {(
                                editingMember
                                  ? editingMember.name
                                  : about.team[about.team.length - 1]?.name
                              )
                                ? (editingMember
                                    ? editingMember.name
                                    : about.team[about.team.length - 1]?.name
                                  )
                                    .charAt(0)
                                    .toUpperCase()
                                : "?"}
                            </div>
                          )}
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            disabled={
                              uploadingImages[
                                editingMember
                                  ? editingMember.index
                                  : about.team.length - 1
                              ]
                            }
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Validate file size (max 10MB)
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error(
                                    "Image size must be less than 10MB"
                                  );
                                  return;
                                }

                                // Validate file type
                                const allowedTypes = [
                                  "image/jpeg",
                                  "image/jpg",
                                  "image/png",
                                  "image/gif",
                                  "image/webp",
                                ];
                                if (!allowedTypes.includes(file.type)) {
                                  toast.error(
                                    "Please select a valid image file (JPEG, PNG, GIF, or WebP)"
                                  );
                                  return;
                                }

                                console.log("File selected for upload:", {
                                  name: file.name,
                                  size: file.size,
                                  type: file.type,
                                });

                                if (editingMember) {
                                  // Handle image upload for editing member
                                  handleImageUploadForEdit(
                                    file,
                                    editingMember.index
                                  );
                                } else {
                                  handleImageUpload(
                                    file,
                                    about.team.length - 1
                                  );
                                }
                              }
                            }}
                            className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold ${
                              uploadingImages[
                                editingMember
                                  ? editingMember.index
                                  : about.team.length - 1
                              ]
                                ? "file:bg-gray-100 file:text-gray-400 cursor-not-allowed"
                                : "file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                            }`}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Upload a square image (recommended: 400x400px)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Social Links */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Social Links
                      </label>
                      <input
                        type="url"
                        placeholder="LinkedIn Profile URL"
                        value={
                          editingMember
                            ? editingMember.linkedin
                            : about.team[about.team.length - 1]?.linkedin || ""
                        }
                        onChange={(e) => {
                          if (editingMember) {
                            setEditingMember({
                              ...editingMember,
                              linkedin: e.target.value,
                            });
                          } else {
                            handleArrayChange(
                              "team",
                              about.team.length - 1,
                              "linkedin",
                              e.target.value
                            );
                          }
                        }}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                      <input
                        type="url"
                        placeholder="Twitter Profile URL"
                        value={
                          editingMember
                            ? editingMember.twitter
                            : about.team[about.team.length - 1]?.twitter || ""
                        }
                        onChange={(e) => {
                          if (editingMember) {
                            setEditingMember({
                              ...editingMember,
                              twitter: e.target.value,
                            });
                          } else {
                            handleArrayChange(
                              "team",
                              about.team.length - 1,
                              "twitter",
                              e.target.value
                            );
                          }
                        }}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    {editingMember ? (
                      <>
                        <button
                          type="button"
                          onClick={handleUpdateMember}
                          className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
                        >
                          Update Member
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={async () => {
                            const memberIndex = about.team.length - 1;
                            await handleSaveMember(memberIndex);
                          }}
                          className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
                        >
                          Save Member
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            removeArrayItem("team", about.team.length - 1);
                            setShowMemberForm(false);
                          }}
                          className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Partners */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Partners
              </label>
              <button
                type="button"
                onClick={() =>
                  addArrayItem("partners", {
                    name: "",
                    description: "",
                    website: "",
                  })
                }
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                Add Partner
              </button>
            </div>
            {about.partners.map((partner, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border mb-4">
                <div className="grid grid-cols-1 gap-4">
                  <input
                    type="text"
                    placeholder="Partner Name"
                    value={partner.name || ""}
                    onChange={(e) =>
                      handleArrayChange(
                        "partners",
                        index,
                        "name",
                        e.target.value
                      )
                    }
                    className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                  <textarea
                    placeholder="Partner Description"
                    value={partner.description || ""}
                    onChange={(e) =>
                      handleArrayChange(
                        "partners",
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    rows={2}
                    className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                  <input
                    type="url"
                    placeholder="Partner Website URL"
                    value={partner.website || ""}
                    onChange={(e) =>
                      handleArrayChange(
                        "partners",
                        index,
                        "website",
                        e.target.value
                      )
                    }
                    className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => removeArrayItem("partners", index)}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-800 bg-red-50 rounded"
                  >
                    Remove Partner
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSavePartner(index)}
                    className="px-3 py-1 text-sm text-teal-600 hover:text-teal-800 bg-teal-50 rounded"
                  >
                    Save Partner
                  </button>
                  {savedItems.partners[index] && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      ✓ Saved
                    </span>
                  )}
                </div>
              </div>
            ))}
            {about.partners.length === 0 && (
              <p className="text-gray-500 text-center py-4">
                No partners added yet. Click "Add Partner" to get started.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 font-medium"
            >
              {saving ? "Saving Changes..." : "Save All Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default About;
